import dayjs from 'dayjs';
import { t } from '../i18n';
import { getToken, removeAuth } from './auth';
import { getMessageApi } from './messageHolder';
import { buildWebRequestHeaders } from './webRequestHeaders';

export interface ExportUsageParams {
    startDate?: string;
    endDate?: string;
    timezone?: string;
}

export interface OrderCancelCreateResponse {
    taskId: string;
    status: string;
    totalCount: number;
    successCount: number;
    failedCount: number;
    ignoredCount: number;
}

export interface OrderCancelFileUrlResponse {
    url: string;
    expiresInMinutes: number;
}

interface PortalErrorBody {
    code?: string;
    messageKey?: string;
    message?: string;
}

export class PortalRestError extends Error {
    body: PortalErrorBody;
    status: number;

    constructor(message: string, body: PortalErrorBody, status: number) {
        super(message);
        this.name = 'PortalRestError';
        this.body = body;
        this.status = status;
        Object.setPrototypeOf(this, PortalRestError.prototype);
    }
}

const parseFilename = (disposition: string | null, fallback: string): string => {
    if (!disposition) return fallback;
    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match) {
        try {
            return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ''));
        } catch {
            // fall through
        }
    }
    const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
    return plainMatch ? plainMatch[1].trim() : fallback;
};

const downloadBlob = (blob: Blob, filename: string): void => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
};

const authHeaders = (): Record<string, string> => {
    const token = getToken();
    const headers: Record<string, string> = { ...buildWebRequestHeaders() };
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
};

const handleErrorResponse = async (res: Response): Promise<PortalErrorBody> => {
    const msg = getMessageApi();
    let body: PortalErrorBody = {};
    try {
        body = await res.json();
    } catch {
        msg?.error(t('errors.network') as string);
        return body;
    }

    const translated = body.messageKey ? (t(body.messageKey) as string) : '';
    const fallback = body.message || (t('errors.network') as string);

    switch (body.code) {
        case 'UNAUTHORIZED':
            msg?.error(translated || (t('errors.unauthorized') as string));
            setTimeout(() => {
                removeAuth();
                window.location.href = '/ClientCenter/login';
            }, 3000);
            return body;
        case 'PERMISSION':
        case 'VALIDATION_ERROR':
        case 'BUSINESS_ERROR':
            msg?.error(translated || fallback);
            return body;
        default:
            msg?.error(translated || fallback);
            return body;
    }
};

export const exportUsageRecords = async (params: ExportUsageParams): Promise<void> => {
    const query = new URLSearchParams();
    if (params.startDate) query.set('startDate', params.startDate);
    if (params.endDate) query.set('endDate', params.endDate);
    if (params.timezone) query.set('timezone', params.timezone);

    let res: Response;
    try {
        res = await fetch(`/api/portal/usage/export?${query.toString()}`, {
            method: 'GET',
            headers: authHeaders(),
        });
    } catch {
        getMessageApi()?.error(t('errors.network') as string);
        return;
    }

    const contentType = res.headers.get('content-type') || '';
    if (!res.ok || contentType.includes('application/json')) {
        await handleErrorResponse(res);
        return;
    }

    const blob = await res.blob();
    const filename = parseFilename(
        res.headers.get('content-disposition'),
        `usage_records_${dayjs().format('YYYYMMDD_HHmmss')}.csv`,
    );
    downloadBlob(blob, filename);
};

export const uploadOrderCancelTask = async (file: File): Promise<OrderCancelCreateResponse> => {
    const form = new FormData();
    form.append('file', file);

    let res: Response;
    try {
        res = await fetch('/api/portal/order-cancel/tasks', {
            method: 'POST',
            headers: authHeaders(),
            body: form,
        });
    } catch {
        getMessageApi()?.error(t('errors.network') as string);
        throw new Error('network error');
    }

    if (!res.ok) {
        const body = await handleErrorResponse(res);
        throw new PortalRestError('upload failed', body, res.status);
    }
    return res.json();
};

export const downloadOrderCancelDetails = async (taskId: string): Promise<void> => {
    let res: Response;
    try {
        res = await fetch(`/api/portal/order-cancel/tasks/${taskId}/details/export`, {
            method: 'GET',
            headers: authHeaders(),
        });
    } catch {
        getMessageApi()?.error(t('errors.network') as string);
        return;
    }

    const contentType = res.headers.get('content-type') || '';
    if (!res.ok || contentType.includes('application/json')) {
        await handleErrorResponse(res);
        return;
    }

    const blob = await res.blob();
    const filename = parseFilename(
        res.headers.get('content-disposition'),
        `order_cancel_details_${taskId}_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`,
    );
    downloadBlob(blob, filename);
};

export const downloadOrderCancelTemplate = async (): Promise<void> => {
    let res: Response;
    try {
        res = await fetch('/api/portal/order-cancel/template', {
            method: 'GET',
            headers: authHeaders(),
        });
    } catch {
        getMessageApi()?.error(t('errors.network') as string);
        return;
    }

    const contentType = res.headers.get('content-type') || '';
    if (!res.ok || contentType.includes('application/json')) {
        await handleErrorResponse(res);
        return;
    }

    const blob = await res.blob();
    const filename = parseFilename(
        res.headers.get('content-disposition'),
        `order_cancel_template_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`,
    );
    downloadBlob(blob, filename);
};

export interface WeighingCreateResponse {
    taskId: string;
    status: string;
    totalCount: number;
    successCount: number;
    failedCount: number;
    ignoredCount: number;
}

export const uploadWeighingTask = async (file: File): Promise<WeighingCreateResponse> => {
    const form = new FormData();
    form.append('file', file);

    let res: Response;
    try {
        res = await fetch('/api/portal/weighing/tasks', {
            method: 'POST',
            headers: authHeaders(),
            body: form,
        });
    } catch {
        getMessageApi()?.error(t('errors.network') as string);
        throw new Error('network error');
    }

    if (!res.ok) {
        const body = await handleErrorResponse(res);
        throw new PortalRestError('upload failed', body, res.status);
    }
    return res.json();
};

export const downloadWeighingDetails = async (taskId: string): Promise<void> => {
    let res: Response;
    try {
        res = await fetch(`/api/portal/weighing/tasks/${taskId}/details/export`, {
            method: 'GET',
            headers: authHeaders(),
        });
    } catch {
        getMessageApi()?.error(t('errors.network') as string);
        return;
    }

    const contentType = res.headers.get('content-type') || '';
    if (!res.ok || contentType.includes('application/json')) {
        await handleErrorResponse(res);
        return;
    }

    const blob = await res.blob();
    const filename = parseFilename(
        res.headers.get('content-disposition'),
        `weighing_details_${taskId}_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`,
    );
    downloadBlob(blob, filename);
};

export const downloadWeighingTemplate = async (): Promise<void> => {
    let res: Response;
    try {
        res = await fetch('/api/portal/weighing/template', {
            method: 'GET',
            headers: authHeaders(),
        });
    } catch {
        getMessageApi()?.error(t('errors.network') as string);
        return;
    }

    const contentType = res.headers.get('content-type') || '';
    if (!res.ok || contentType.includes('application/json')) {
        await handleErrorResponse(res);
        return;
    }

    const blob = await res.blob();
    const filename = parseFilename(
        res.headers.get('content-disposition'),
        `weighing_template_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`,
    );
    downloadBlob(blob, filename);
};

export interface WeighingImgDetail {
    id: string;
    taskId: string;
    firstTrackingNumber: string;
    compressedFileSize: number | null;
    downloadUrl: string | null;
    createdAt: string | null;
    uploadCompletedAt: string | null;
}

export const getWeighingImgDetails = async (taskId: string): Promise<WeighingImgDetail[]> => {
    let res: Response;
    try {
        res = await fetch(`/api/portal/weighing/tasks/${taskId}/img-details`, {
            method: 'GET',
            headers: authHeaders(),
        });
    } catch {
        getMessageApi()?.error(t('errors.network') as string);
        throw new Error('network error');
    }
    if (!res.ok) {
        await handleErrorResponse(res);
        throw new PortalRestError('fetch img details failed', {}, res.status);
    }
    return res.json();
};

export const getOrderCancelSourceFileUrl = async (taskId: string): Promise<OrderCancelFileUrlResponse> => {
    let res: Response;
    try {
        res = await fetch(`/api/portal/order-cancel/tasks/${taskId}/source-file-url`, {
            method: 'GET',
            headers: authHeaders(),
        });
    } catch {
        getMessageApi()?.error(t('errors.network') as string);
        throw new Error('network error');
    }

    if (!res.ok) {
        await handleErrorResponse(res);
        throw new Error('download url failed');
    }
    return res.json();
};
